import clsx from 'clsx';
import { type MutableRefObject, useContext } from 'react';
import { useObjectRef } from 'react-aria';

import { assert } from '@gitbook/util-ts';

import { LoadingIndicator } from '../Loading';
import { AutoCompleteStateContext } from './AutoComplete';

export type MenuSearchProps = Pick<InternalMenuSearchProps, 'label' | 'isPending'>;

export function MenuSearch(props: MenuSearchProps) {
    return <InternalMenuSearch {...props} />;
}

export function HiddenMenuSearch() {
    const ctx = useContext(AutoCompleteStateContext);
    assert(ctx, 'Must be used in a Menu');
    const isHidden = Boolean(ctx && !ctx?.isTouched);
    return <InternalMenuSearch isHidden={isHidden} />;
}

type InternalMenuSearchProps = {
    /**
     * Label used as aria-label and placeholder.
     */
    label?: string | undefined;

    /**
     * Show a pending indicator on the end.
     * @default false
     */
    isPending?: boolean;

    /**
     * Hide it.
     */
    isHidden?: boolean;
};

function InternalMenuSearch(props: InternalMenuSearchProps) {
    const { label = 'Filter…', isHidden, isPending } = props;
    const ctx = useContext(AutoCompleteStateContext);
    const localRef = useObjectRef(ctx?.inputRef);
    assert(ctx, 'Must be used in a Menu');
    return (
        <div
            className={clsx(
                isHidden
                    ? '-m-px -left-[9999px] absolute size-px overflow-hidden whitespace-nowrap border-0 p-0 [clip:rect(0,0,0,0)]'
                    : 'relative flex shrink-0'
            )}
            data-menu-search=""
            data-hidden={isHidden ? 'true' : 'false'}
        >
            <form className="contents" autoComplete="off">
                <input
                    ref={localRef as MutableRefObject<HTMLInputElement>}
                    data-1p-ignore
                    data-lpignore
                    spellCheck={false}
                    autoComplete="off"
                    autoCorrect="off"
                    className={clsx(
                        'bg-transparent',
                        'w-0 min-w-0 flex-1 border-b px-3.5 py-2 text-ui-base',
                        'appearance-none [&::-webkit-search-cancel-button]:hidden',
                        isPending && 'pr-7'
                    )}
                    placeholder={label}
                    aria-label={label}
                    type="search"
                    value={ctx.value}
                    onChange={(event) => {
                        ctx.setValue(event.target.value);
                    }}
                />
                <LoadingIndicator
                    className={clsx(
                        'pointer-events-none absolute top-2 right-2 mt-px size-3 transition',
                        !isPending && 'opacity-0'
                    )}
                />
            </form>
        </div>
    );
}
